import React, { Dispatch, SetStateAction, useContext, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import Swal from 'sweetalert2';

import ConsultantCheckoutContext from 'src/contexts/consultantCheckout';

import { ICustomerData, IServiceData } from 'src/environments/CheckoutConsultantAPP/interfaces';
import { apiCheckoutConsultant } from 'src/environments/CheckoutConsultantAPP/api';
import createPagarmeCardToken from 'src/utils/createPagarmeCardToken';
import InputCheckout from 'src/components/functional/InputCheckout';
import SelectExpirationDate from './components/SelectExpirationDate';

import {
  BackButton,
  ButtonContinue,
  ButtonPayment,
  ContainerCreditCardForm,
  ContainerSelect,
  CreditCardIcon,
  IconArrowDown,
  LabelInstallment,
  LoadingGif,
  SelectInstallments,
  SideBySide,
  TitlePayment,
} from './style';

import whiteCreditCardIconSVG from '../../../../../../../../assets/images/subscripiton-checkout-icons/white-credit-card-icon.svg';
import iconArrowDownSVG from '../../../../../../../../assets/images/subscripiton-checkout-icons/arrow-down-icon.svg';
import loadingGIF from '../../../../../../../../assets/gifs/loading.png';

type Props = {
  customerData: ICustomerData;
  serviceData: IServiceData;
  setShowPaymentMethod: Dispatch<SetStateAction<string>>;
};

export default function PurchasePaymentCreditCard({
  customerData,
  serviceData,
  setShowPaymentMethod,
}: Props): JSX.Element {
  const { fullAppointmentData } = useContext(ConsultantCheckoutContext);

  const [cardData, setCardData] = useState({
    cardHolderName: '',
    cardNumber: '',
    expMonth: '',
    expYear: '',
    cardCvv: '',
    amount: 0,
    installments: 1,
  });
  const [isDisabled, setIsDisabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const { token } = useParams();

  const onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = event.target;
    setCardData({
      ...cardData,
      [name]: value,
    });
  };

  const { mutate: createCreditCardOrderMutate } = useMutation(
    async ({ consultantServiceData, cardToken }: any) =>
      apiCheckoutConsultant.createCreditCardOrder(consultantServiceData, cardToken),
    {
      onError: () => {
        void Swal.fire({
          icon: 'error',
          title: 'Cartão inválido ou bloqueado!',
          text: 'Por favor, confira os dados do seu cartão!',
          iconColor: 'black',
          confirmButtonColor: 'black',
        });
        setLoading(false);
        setIsDisabled(false);
      },
      onSuccess: async () => {
        navigate(`/pagamento-concluido/servico-consultora/${token}`);
      },
    }
  );

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setIsDisabled(true);
    setLoading(true);

    if (fullAppointmentData.paymentStatus === 'Aprovado') {
      void Swal.fire({
        icon: 'error',
        title: 'Esse link já foi pago!',
        text: 'Por favor, entre em contato com sua consultora para confirmar o pagamento!',
        iconColor: 'black',
        confirmButtonColor: 'black',
      });
      setIsDisabled(false);
      setLoading(false);

      return;
    }

    const paymentCardData = {
      ...cardData,
      cardNumber: cardData.cardNumber.replaceAll(' ', ''),
    };

    const consultantServiceData = {
      ...customerData,
      customerPhone: customerData.customerPhone.replace(/[^a-zA-Z0-9]/g, '').replace(' ', ''),
      customerCpf: customerData.customerCpf.replace(/[^a-zA-Z0-9]/g, ''),
      zipCode: customerData.zipCode.replace('-', ''),
      installments: Number(cardData.installments),
      ...serviceData,
    };

    const cardToken = await createPagarmeCardToken(paymentCardData);

    if (cardToken === 'Error') {
      setLoading(false);
      setIsDisabled(false);
      return;
    }

    createCreditCardOrderMutate({ consultantServiceData, cardToken });
  };

  return (
    <ContainerCreditCardForm onSubmit={handleSubmitForm}>
      <TitlePayment>Pagamento</TitlePayment>
      <ButtonPayment>
        <CreditCardIcon src={whiteCreditCardIconSVG} alt="Ícone de cartão de crédito" />
        <p>Cartão de Crédito</p>
        <BackButton
          type="button"
          onClick={() => {
            setShowPaymentMethod('');
          }}
        >
          Voltar
        </BackButton>
      </ButtonPayment>
      <ContainerSelect>
        <LabelInstallment>Quantidade de parcelas</LabelInstallment>
        <SelectInstallments name="installments" required onChange={onChange}>
          {[1, 2, 3, 4, 5].map((installment, index) => (
            <option key={index} value={installment}>
              {installment}x de{' '}
              {(serviceData.serviceValue / Number(installment)).toLocaleString('pt-br', {
                style: 'currency',
                currency: 'BRL',
              })}
            </option>
          ))}
        </SelectInstallments>
        <IconArrowDown src={iconArrowDownSVG} />
      </ContainerSelect>
      <InputCheckout
        label="Número do Cartão"
        name="cardNumber"
        mask="9999 9999 9999 9999"
        inputMode="numeric"
        value={cardData.cardNumber}
        inputWidth="89.333vw"
        onChange={onChange}
        placeholder="0000 0000 0000 0000"
        colorBorder={cardData.cardNumber !== '' ? '#000000' : '#c7c7c7'}
      />
      <InputCheckout
        label="Nome no Cartão"
        inputWidth="89.333vw"
        name="cardHolderName"
        value={cardData.cardHolderName}
        placeholder="Nome e Sobrenome"
        maxLength={42}
        onChange={onChange}
        colorBorder={cardData.cardHolderName !== '' ? '#000000' : '#c7c7c7'}
      />
      <SideBySide>
        <SelectExpirationDate onChange={onChange} />
        <InputCheckout
          label="CVV"
          inputWidth="30.666vw"
          name="cardCvv"
          placeholder="000"
          value={cardData.cardCvv}
          maxLength={4}
          onChange={onChange}
          inputMode="numeric"
          colorBorder={cardData.cardCvv !== '' ? '#000000' : '#c7c7c7'}
        />
      </SideBySide>
      <ButtonContinue type="submit" disabled={isDisabled}>
        {loading ? <LoadingGif src={loadingGIF} alt="gif de carregamento" /> : 'FINALIZAR'}
      </ButtonContinue>
    </ContainerCreditCardForm>
  );
}
